import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Rating from "@mui/material/Rating";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import Title from "../components/header/Title";
import ReviewService, { IReview } from "../services/ReviewService";
import ProductService from "../services/ProductService";
import UserService from "../services/UserService";
import { unwrapList, getErrorMessage, resolveImageUrl } from "../services/apiUtils";

const extractReviewImages = (r: any): string[] => {
  const raw =
    r.mediaList ||
    r.images ||
    r.media ||
    r.ratingMedia ||
    r.reviewImages ||
    r.imageUrls ||
    r.attachments ||
    r.files ||
    [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item: any) => {
      if (!item) return "";
      if (typeof item === "string") return resolveImageUrl(item);
      return resolveImageUrl(item.col1 || item.dataUrl || item.url || item.path || item.filename);
    })
    .filter((url: string) => Boolean(url));
};

const formatReviewDate = (value: any): string => {
  if (!value) return "-";
  if (Array.isArray(value)) {
    const [y, m, d] = value;
    if (!y) return "-";
    const parsed = new Date(y, (m || 1) - 1, d || 1);
    return isNaN(parsed.getTime()) ? "-" : parsed.toLocaleDateString();
  }
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString();
};

const Reviews: React.FC = () => {
  const [reviews, setReviews] = useState<IReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadReviews = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      ReviewService.getAllReviews(),
      ProductService.getProducts().catch(() => null),
      UserService.getUsers().catch(() => null),
    ])
      .then(([reviewsRes, productsRes, usersRes]: any) => {
        const rawReviews = unwrapList<any>(reviewsRes);
        const products = productsRes ? unwrapList<any>(productsRes) : [];
        const users = usersRes ? unwrapList<any>(usersRes) : [];

        const productMap = new Map<number, string>(
          products.map((p: any) => [p.productId, p.title])
        );
        const userMap = new Map<number, string>(
          users.map((u: any) => [u.userId, u.name || u.username || u.emailId])
        );

        const mapped: IReview[] = rawReviews.map((r: any) => ({
          ...r,
          ratingId: r.ratingId ?? r.productRatingId,
          userName:
            r.userName || userMap.get(r.userId) || (r.userId ? `User #${r.userId}` : "-"),
          productTitle:
            r.productTitle ||
            productMap.get(r.productId) ||
            (r.productId ? `Product #${r.productId}` : "-"),
          comment: r.comment ?? r.review ?? "",
          createdAt: formatReviewDate(r.createdAt ?? r.ratingDate),
          images: extractReviewImages(r),
        }));

        // Show the newest review first. ratingDate is often missing/null in
        // the data, so we can't reliably sort by date — productRatingId
        // is auto-incrementing, so the highest id is always the latest review.
        mapped.sort((a, b) => (b.ratingId || 0) - (a.ratingId || 0));

        setReviews(mapped);
      })
      .catch((err: any) =>
        setError(getErrorMessage(err, "Reviews could not be loaded."))
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadReviews();
  }, []);

  const handleDelete = (review: IReview) => {
    setDeletingId(review.ratingId);
    ReviewService.deleteReview(review.ratingId)
      .then(() => {
        setReviews((prev) => prev.filter((r) => r.ratingId !== review.ratingId));
      })
      .catch((err: any) => setError(getErrorMessage(err, "Could not delete this review.")))
      .finally(() => setDeletingId(null));
  };

  const columns: GridColDef[] = [
    { field: "ratingId", headerName: "ID", flex: 0.4 },
    { field: "userName", headerName: "Customer", flex: 1 },
    { field: "productTitle", headerName: "Product", flex: 1 },
    {
      field: "rating",
      headerName: "Rating",
      flex: 0.8,
      renderCell: (params) => <Rating value={Number(params.value) || 0} readOnly size="small" />,
    },
    { field: "comment", headerName: "Comment", flex: 1.6 },
    {
      field: "images",
      headerName: "Images",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const images: string[] = params.value || [];
        if (!images.length) {
          return (
            <Box sx={{ color: "text.disabled", fontSize: 12 }}>No image</Box>
          );
        }
        return (
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", py: 0.5 }}>
            {images.slice(0, 3).map((src, idx) => (
              <Tooltip title="Click to view full size" arrow key={idx}>
                <Box
                  component="img"
                  src={src}
                  alt={`review-image-${idx}`}
                  onClick={() => window.open(src, "_blank", "noopener,noreferrer")}
                  sx={{
                    width: 36,
                    height: 36,
                    objectFit: "cover",
                    borderRadius: 1,
                    cursor: "pointer",
                    border: "1px solid rgba(0,0,0,0.12)",
                  }}
                />
              </Tooltip>
            ))}
            {images.length > 3 && (
              <Box sx={{ fontSize: 12, color: "text.secondary" }}>+{images.length - 3}</Box>
            )}
          </Box>
        );
      },
    },
    { field: "createdAt", headerName: "Posted On", flex: 0.8 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Delete review">
          <IconButton
            size="small"
            color="error"
            disabled={deletingId === params.row.ratingId}
            onClick={() => handleDelete(params.row)}
          >
            <DeleteRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <Title title="Reviews" subtitle="Customer ratings and reviews across all products" />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ height: 600, width: "100%", bgcolor: "#fff" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={reviews}
            columns={columns}
            getRowId={(row) => row.ratingId}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            rowHeight={56}
            initialState={{
              sorting: { sortModel: [{ field: "ratingId", sort: "desc" }] },
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default Reviews;
